import React, { useEffect, useState, useRef } from "react";
import { api, fmtRelative } from "../lib/api";
import { MessageSquare, X, Send, Circle, Check, CheckCheck, Users } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useWS } from "../lib/ws";
import { playChat } from "../lib/sound";

const GROUP = { id: "__group__", name: "Team", group: true };

export default function ChatWidget() {
  const { user } = useAuth();
  const { subscribe } = useWS();
  const [open, setOpen] = useState(false);
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [typing, setTyping] = useState(false);
  const [groupMeta, setGroupMeta] = useState({ unread: 0, last_message: null });
  const scrollerRef = useRef(null);
  const typingHideRef = useRef(null);
  const lastTypeSentRef = useRef(0);

  const loadConvs = async () => {
    try {
      const [r, g] = await Promise.all([
        api.get("/chat/conversations"),
        api.get("/chat/group/meta").catch(() => ({ data: { unread: 0, last_message: null } })),
      ]);
      setConvs(r.data || []);
      setGroupMeta(g.data || { unread: 0, last_message: null });
    } catch {}
  };

  const loadMsgs = async (id) => {
    try {
      const r = await api.get(`/chat/messages/${id}`);
      setMsgs(r.data || []);
      setTimeout(()=>scrollerRef.current?.scrollTo({top: 99999, behavior: "smooth"}), 50);
    } catch {}
  };

  const loadGroup = async () => {
    try {
      const r = await api.get("/chat/group");
      setMsgs(r.data || []);
      setTimeout(()=>scrollerRef.current?.scrollTo({top: 99999, behavior: "smooth"}), 50);
    } catch {}
  };

  useEffect(() => {
    if (!user) return;
    loadConvs();
    // WS: update conversations & active thread in real-time
    const unsub = subscribe("chat", (m) => {
      loadConvs();
      // Play the chat sound for any incoming message (not ones I sent myself).
      if (m.from_user_id && m.from_user_id !== user.id) playChat();
      // They sent a message → they've stopped typing.
      if (active && m.from_user_id === active.id) setTyping(false);
      if (active && (m.from_user_id === active.id || m.to_user_id === active.id)) {
        setMsgs(prev => {
          if (prev.some(x => x.id === m.id)) return prev;
          return [...prev, m];
        });
        setTimeout(()=>scrollerRef.current?.scrollTo({top: 99999, behavior: "smooth"}), 50);
        // mark as read if it's the active conversation
        if (m.from_user_id === active.id) api.get(`/chat/messages/${active.id}`).catch(()=>{});
      }
    });
    // Live read-receipts: when the other person reads my messages, flip them to "Seen".
    const unsubRead = subscribe("chat_read", (d) => {
      setMsgs(prev => prev.map(x =>
        ((d.message_ids && d.message_ids.includes(x.id)) ||
         (x.from_user_id === user.id && x.to_user_id === d.reader_id && !x.read))
          ? { ...x, read: true, read_at: d.at } : x));
    });
    // Live "typing…" indicator for the open conversation.
    const unsubTyping = subscribe("typing", (d) => {
      if (active && d.from_user_id === active.id) {
        setTyping(true);
        if (typingHideRef.current) clearTimeout(typingHideRef.current);
        typingHideRef.current = setTimeout(() => setTyping(false), 3500);
      }
    });
    // Team group chat — everyone receives it live.
    const unsubGroup = subscribe("group_chat", (m) => {
      loadConvs();
      if (m.from_user_id !== user.id) playChat();
      if (active && active.group) {
        setTyping(false);
        setMsgs(prev => prev.some(x => x.id === m.id) ? prev : [...prev, m]);
        setTimeout(()=>scrollerRef.current?.scrollTo({top: 99999, behavior: "smooth"}), 50);
        if (m.from_user_id !== user.id) api.get("/chat/group").catch(()=>{}); // mark read
      }
    });
    // Lightweight poll fallback every 60s
    const id = setInterval(() => { loadConvs(); if (active) { active.group ? loadGroup() : loadMsgs(active.id); } }, 60000);
    return () => { clearInterval(id); unsub(); unsubRead(); unsubTyping(); unsubGroup(); if (typingHideRef.current) clearTimeout(typingHideRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, active, subscribe]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    const body = text.trim();
    setText("");
    if (active.group) {
      await api.post("/chat/group", { message: body });
      loadGroup();
    } else {
      await api.post("/chat/messages", { to_user_id: active.id, message: body });
      loadMsgs(active.id);
    }
    loadConvs();
  };

  // Throttled "typing…" ping — at most once every 2.5s while the user types.
  const sendTyping = () => {
    if (!active) return;
    const now = Date.now();
    if (now - lastTypeSentRef.current < 2500) return;
    lastTypeSentRef.current = now;
    api.post("/chat/typing", { to_user_id: active.id }).catch(() => {});
  };

  const openConv = (c) => { setActive(c); setTyping(false); if (c.group) loadGroup(); else loadMsgs(c.id); };

  const totalUnread = convs.reduce((s,c)=>s+(c.unread||0), 0) + (groupMeta.unread || 0);

  if (!user) return null;

  return (
    <>
      {!open && (
        <button data-testid="chat-fab" onClick={()=>{setOpen(true); loadConvs();}}
          className="chat-fab h-14 w-14 rounded-full bg-[#4f87ff] hover:bg-[#3a6fe0] text-white shadow-xl flex items-center justify-center transition-all">
          <MessageSquare size={20}/>
          {totalUnread > 0 && <span className="absolute -top-1 -right-1 bg-white text-[#4f87ff] text-[10px] font-bold h-5 min-w-5 px-1 rounded-full flex items-center justify-center">{totalUnread}</span>}
        </button>
      )}
      {open && (
        <div data-testid="chat-widget" className="chat-fab w-[92vw] sm:w-[380px] h-[70vh] sm:h-[520px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
          <div className="h-14 px-4 flex items-center justify-between border-b bg-[#0E1423] text-white">
            <div className="font-medium flex items-center gap-1.5" style={{fontFamily:'Outfit'}}>{active?.group && <Users size={15}/>}{active ? active.name : "Messages"}</div>
            <div className="flex items-center gap-2">
              {active && <button onClick={()=>{setActive(null); setMsgs([]); setTyping(false);}} className="text-white/70 hover:text-white text-xs">Back</button>}
              <button data-testid="chat-close" onClick={()=>setOpen(false)} className="text-white/70 hover:text-white"><X size={16}/></button>
            </div>
          </div>
          {!active && (
            <div className="flex-1 overflow-y-auto">
              <button data-testid="chat-conv-group" onClick={()=>openConv(GROUP)} className="w-full text-left px-4 py-3 border-b hover:bg-gray-50 flex items-center gap-3 bg-[#f6f9ff]">
                <div className="h-10 w-10 rounded-full bg-[#4f87ff] text-white flex items-center justify-center flex-shrink-0"><Users size={18}/></div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center">
                    <div className="font-medium text-sm text-gray-900 truncate">Team <span className="text-[10px] text-gray-400 font-normal">· everyone</span></div>
                    {groupMeta.unread > 0 && <span className="bg-[#4f87ff] text-white text-[10px] font-bold h-5 min-w-5 px-1 rounded-full flex items-center justify-center">{groupMeta.unread}</span>}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{groupMeta.last_message ? `${(groupMeta.last_message.from_user_name||"").split(" ")[0]}: ${groupMeta.last_message.message}` : <span className="italic">Broadcast to the whole team 📣</span>}</div>
                </div>
              </button>
              {convs.map(c => (
                <button key={c.id} data-testid={`chat-conv-${c.id}`} onClick={()=>openConv(c)} className="w-full text-left px-4 py-3 border-b hover:bg-gray-50 flex items-center gap-3">
                  <div className="relative h-10 w-10 rounded-full bg-[#EEF2FF] text-[#4f87ff] flex items-center justify-center font-semibold">
                    {c.name?.charAt(0).toUpperCase()}
                    <span className={`absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full ring-2 ring-white ${c.online ? "bg-green-500" : "bg-gray-300"}`}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <div className="font-medium text-sm text-gray-900 truncate">{c.name}</div>
                      {c.unread > 0 && <span className="bg-[#4f87ff] text-white text-[10px] font-bold h-5 min-w-5 px-1 rounded-full flex items-center justify-center">{c.unread}</span>}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{c.last_message?.message || <span className="italic">Say hi 👋</span>}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {active && (
            <>
              <div ref={scrollerRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50">
                {msgs.length === 0 && <div className="text-center text-xs text-gray-400 p-4">Start the conversation</div>}
                {msgs.map(m => {
                  const mine = m.from_user_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${mine ? "bg-[#4f87ff] text-white rounded-br-sm" : "bg-white border border-gray-200 text-gray-900 rounded-bl-sm"}`}>
                        {active.group && !mine && <div className="text-[10px] font-semibold text-[#4f87ff] mb-0.5">{m.from_user_name}</div>}
                        {m.message}
                        <div className={`text-[10px] mt-1 flex items-center gap-1 ${mine ? "justify-end text-white/70" : "text-gray-400"}`}>
                          <span>{fmtRelative(m.created_at)}</span>
                          {mine && !active.group && (m.read
                            ? <span className="flex items-center gap-0.5 text-white" title={m.read_at ? `Seen at ${new Date(m.read_at).toLocaleString("en-IN",{day:"numeric",month:"short",hour:"numeric",minute:"2-digit",hour12:true})}` : "Seen"}><CheckCheck size={13}/> Seen</span>
                            : <span title="Sent"><Check size={12} className="text-white/60" /></span>)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {typing && (
                  <div className="flex justify-start" data-testid="chat-typing">
                    <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-3 py-2 flex items-center gap-1">
                      <span className="text-[11px] text-gray-400 mr-1">typing</span>
                      <span className="h-1.5 w-1.5 bg-gray-400 rounded-full animate-bounce" style={{animationDelay:'0ms'}}/>
                      <span className="h-1.5 w-1.5 bg-gray-400 rounded-full animate-bounce" style={{animationDelay:'150ms'}}/>
                      <span className="h-1.5 w-1.5 bg-gray-400 rounded-full animate-bounce" style={{animationDelay:'300ms'}}/>
                    </div>
                  </div>
                )}
              </div>
              <form onSubmit={send} className="p-2 border-t bg-white flex gap-2">
                <input data-testid="chat-input" value={text} onChange={e=>{setText(e.target.value); sendTyping();}} placeholder="Type a message…" className="flex-1 px-3 py-2 rounded-lg bg-gray-100 focus:bg-white border border-transparent focus:border-[#4f87ff] text-sm outline-none"/>
                <button data-testid="chat-send" className="h-10 w-10 rounded-lg bg-[#4f87ff] hover:bg-[#3a6fe0] text-white flex items-center justify-center"><Send size={16}/></button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
